import {Component, ChangeDetectionStrategy} from '@angular/core';
import {AuthService as Auth0Service} from '@auth0/auth0-angular';
import {Router} from '@angular/router';
import {filter, switchMap, take} from 'rxjs/operators';
import {AuthService} from './core/auth/auth.service';

@Component({
    selector: 'ctv-root',
    template: `
    <div class="app-wrapper mat-app-background">
      <router-outlet></router-outlet>
      <ctv-loader #loader></ctv-loader>
      @if (auth0Service.isAuthenticated$ | async) {
        <ctv-consent-banner></ctv-consent-banner>
      }
    </div>`,
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class AppComponent {
  constructor(
    protected auth0Service: Auth0Service,
    private authService: AuthService,
    private router: Router,
  ) {
    this.auth0Service.error$.subscribe(err => {
      if (err) {
        console.error('Auth0 SDK error:', err);
      }
    });

    // Warm the in-memory access-token cache as soon as Auth0 reports a session so the
    // first page's API calls do not each wait on getAccessTokenSilently.
    this.auth0Service.isAuthenticated$.pipe(
      filter((isAuth) => !!isAuth),
      take(1),
      switchMap(() => this.authService.getAccessToken()),
    ).subscribe();

    // appState$ only emits once Auth0's redirect callback has actually run, carrying whatever
    // `target` AuthService.loginWithRedirect() stashed before sending the user off to Auth0 -
    // without consuming it here, that returnUrl is captured but never used, so everyone lands on
    // the app root after login instead of the deep link (or MFA-interrupted page) they started
    // from. `target` can trace back to a user-supplied `returnUrl` query param (see
    // LoginComponent), so treat it as untrusted: only ever hand Router a same-origin relative path.
    this.auth0Service.appState$.subscribe(appState => {
      const target = appState?.target;
      if (typeof target !== 'string' || !target) {
        return;
      }
      // A target can arrive as the literal hash-routed form (/#/campaigns/123) rather than the
      // canonical Router path (/campaigns/123) - Router.navigateByUrl treats a bare `#` as a URL
      // fragment, not a path separator, so an un-normalized hash form would silently navigate to
      // root instead of the intended page.
      const normalizedTarget = target.startsWith('/#/') ? target.slice(2) : target;
      if (normalizedTarget.startsWith('/') && !normalizedTarget.startsWith('//')) {
        this.router.navigateByUrl(normalizedTarget);
      }
    });
  }
}
